import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import Headline4 from '../Typography/Heading/Headline4';
import Headline6 from '../Typography/Heading/Headline6';
import SpacingContainer from '../SpacingContainer';
import Box from '../Box';
import IconButton from '../Button/IconButton';

const FaqSection = styled.div`
  margin-bottom: 2rem;
`;

interface FaqData {
  id: string;
  title?: string;
  items: {
    question: React.ReactNode;
    answer: React.ReactNode;
    id: string;
  }[];
}

interface BaseSubscriptionFaqProperties {
  faqData: FaqData[];
  faqHeaderLabel: string;
}

interface WithAnchorProperties extends BaseSubscriptionFaqProperties {
  anchorId: string;
  copyLinkAriaLabel: string;
  onCopyLink?: (url: string) => void;
}

interface WithoutAnchorProperties
  extends BaseSubscriptionFaqProperties {
  anchorId?: never;
  copyLinkAriaLabel?: never;
  onCopyLink?: never;
}

type SubscriptionFaqProperties =
  | WithAnchorProperties
  | WithoutAnchorProperties;

const SubscriptionFaq = ({
  faqData,
  faqHeaderLabel,
  anchorId,
  copyLinkAriaLabel,
  onCopyLink,
}: SubscriptionFaqProperties) => {
  const handleCopyAnchorLink = useCallback(() => {
    const { origin, pathname } = window.location;
    const url = `${origin}${pathname}#${anchorId}`;
    navigator.clipboard.writeText(url);
    onCopyLink?.(url);
  }, [anchorId, onCopyLink]);

  return (
    <div id={anchorId}>
      {faqHeaderLabel && (
        <>
          <Headline4>
            {faqHeaderLabel}
            {anchorId && (
              <IconButton
                ariaLabel={copyLinkAriaLabel}
                onClick={handleCopyAnchorLink}
              >
                <LinkIcon />
              </IconButton>
            )}
          </Headline4>
          <SpacingContainer marginBottom="16px" />
        </>
      )}

      {faqData.map((section) => {
        const { id: sectionId, title, items } = section;

        return (
          <FaqSection key={sectionId}>
            {title && (
              <>
                <Headline6 component="h3">{title}</Headline6>
                <SpacingContainer />
              </>
            )}

            {items.map(({ id: itemId, question, answer }) => (
              <Accordion key={itemId}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`${itemId}-content`}
                  id={itemId}
                >
                  <strong>{question}</strong>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>{answer}</Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </FaqSection>
        );
      })}
    </div>
  );
};

export default SubscriptionFaq;
